import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { PageHeader } from "../components/ui";

const Section = ({ title, children }) => (
  <div>
    <h2 className="mb-4 text-2xl font-semibold text-ink">{title}</h2>
    <div className="space-y-4">{children}</div>
  </div>
);

export default function TermsPage() {
  return (
    <>
      <Helmet>
        <title>Terms & Conditions | Chiparama</title>
        <meta
          name="description"
          content="Chiparama's Terms & Conditions governing use of our website and services, including SMS messaging terms and dispute resolution."
        />
        <link rel="canonical" href="https://chiparama.com/terms-and-conditions" />
      </Helmet>

      <PageHeader eyebrow="Legal" title="Terms &amp; Conditions">
        Last updated: August 10, 2026
      </PageHeader>

      <section className="section bg-white">
        <div className="container-x max-w-3xl">
        <div className="space-y-12 leading-relaxed text-slate-600">
          <Section title="Agreement to Terms">
            <p>
              By accessing or using the website of Chiparama Inc ("Chiparama," "we," "us" or "our") ("Site"), you agree to be bound by these Terms &
              Conditions ("Terms"). If you do not agree with any part of these Terms, please do not use our
              Site or services. These Terms apply to all visitors and users of the Site, wherever located,
              including in the United States and Canada.
            </p>
          </Section>

          <Section title="Use of Our Services">
            <p>
              Chiparama provides semiconductor design, verification, embedded engineering, and staffing
              services. Any information submitted through our Site, including via our Contact Us form, is
              used solely to respond to your inquiry and provide the services you request. See our{" "}
              <Link to="/privacy-policy" className="link">
                Privacy Policy
              </Link>{" "}
              for details on how we handle your information.
            </p>
          </Section>

          <Section title="Intellectual Property">
            <p>
              All content on this Site — including text, graphics, logos, images, and software — is the
              property of Chiparama or its licensors and is protected by applicable intellectual property
              laws. You may not reproduce, distribute, modify, or create derivative works from any content
              on this Site without our prior written consent.
            </p>
          </Section>

          <Section title="Acceptable Use">
            <p>When using our Site, you agree not to:</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>Use the Site for any unlawful purpose or in violation of these Terms;</li>
              <li>Attempt to gain unauthorized access to our systems or networks;</li>
              <li>Interfere with or disrupt the Site's functionality or security;</li>
              <li>Submit false, misleading, or fraudulent information through our forms; or</li>
              <li>Use automated means to scrape or extract content from the Site without permission.</li>
            </ul>
          </Section>

          <Section title="SMS/Text Messaging Terms">
            <p>
              By opting in to receive text messages from Chiparama Inc, you agree to receive job opening alerts
              and company updates at the phone number you provide. Message frequency varies.
              Message and data rates may apply. Consent to receive SMS messages is not a condition of
              purchasing any goods or services. You may opt out at any time by replying STOP, and reply HELP
              for assistance. See our{" "}
              <Link to="/privacy-policy" className="link">
                Privacy Policy
              </Link>{" "}
              for details on how your information and SMS consent status are handled.
            </p>
          </Section>

          <Section title="Email Communications">
            <p>
              Emails we send in connection with our services comply with the CAN-SPAM Act (US), including
              accurate sender information, clear identification of any promotional content, our contact
              information, and a working opt-out mechanism.
            </p>
          </Section>

          <Section title="Third-Party Links">
            <p>
              Our Site may contain links to third-party websites or services that are not owned or
              controlled by Chiparama. We have no control over, and assume no responsibility for, the
              content, privacy practices, or terms of any third-party sites.
            </p>
          </Section>

          <Section title="Disclaimer of Warranties">
            <p>
              The Site and its content are provided "as is" and "as available" without warranties of any
              kind, whether express or implied, including but not limited to implied warranties of
              merchantability, fitness for a particular purpose, or non-infringement. We do not warrant that
              the Site will be uninterrupted, secure, or error-free.
            </p>
          </Section>

          <Section title="Limitation of Liability">
            <p>
              To the fullest extent permitted by applicable law, Chiparama shall not be liable for any
              indirect, incidental, special, consequential, or punitive damages, or any loss of profits or
              revenues, arising out of or related to your use of the Site, whether based on warranty,
              contract, tort, or any other legal theory, even if we have been advised of the possibility of
              such damages.
            </p>
          </Section>

          <Section title="Indemnification">
            <p>
              You agree to indemnify and hold harmless Chiparama, its officers, employees, and agents from
              any claims, damages, liabilities, and expenses (including reasonable attorneys' fees) arising
              out of your use of the Site or violation of these Terms.
            </p>
          </Section>

          <Section title="Governing Law & Dispute Resolution">
            <p>
              These Terms are governed by the laws of the State of Texas, USA, without regard to its
              conflict-of-law provisions, for users located in the United States. For users located in
              Canada, nothing in these Terms limits any consumer protection rights you may have under
              applicable Canadian federal or provincial law. Any disputes arising from these Terms or your
              use of the Site shall first be attempted to be resolved informally by contacting us; if
              unresolved, disputes shall be subject to the exclusive jurisdiction of the courts located in
              Texas, USA, except where prohibited by applicable law.
            </p>
          </Section>

          <Section title="Accessibility">
            <p>
              We aim to make our Site accessible to all users. If you encounter accessibility barriers or
              have suggestions for improvement, please let us know using the contact information below.
            </p>
          </Section>

          <Section title="Termination">
            <p>
              We reserve the right to suspend or terminate your access to the Site at any time, without
              notice, for conduct that we believe violates these Terms or is otherwise harmful to other
              users, us, or third parties.
            </p>
          </Section>

          <Section title="Changes to These Terms">
            <p>
              We may update these Terms from time to time. Any changes will be posted on this page with an
              updated "Last updated" date. Your continued use of the Site after changes are posted
              constitutes acceptance of the revised Terms.
            </p>
          </Section>

          <Section title="Contact Us">
            <p>
              If you have questions about these Terms & Conditions, please{" "}
              <Link to="/contact" className="link">
                contact us
              </Link>{" "}
              or email{" "}
              <a href="mailto:contact@chiparama.com" className="link">
                contact@chiparama.com
              </a>
              .
            </p>
          </Section>
        </div>
        </div>
      </section>
    </>
  );
}
